import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Chip,
  Box,
  Typography,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import { green, grey, red, blue } from "@mui/material/colors";
import axios from "axios";
import toast from "react-hot-toast";
import MUIDataTable from "mui-datatables";
import Loader from "../../components/loader/Loader";
import { useForm, Controller } from "react-hook-form";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableRowsIcon from "@mui/icons-material/TableRows";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import { CircularProgress } from "@mui/material";
import { usePermission } from "../../hooks/usePermission";

const WhatsAppCampaignScreen = () => {
  // State declarations
  const permissions = usePermission("CampaignScreen");
  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templatesSyncing, setTemplatesSyncing] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [campaignCount, setCampaignCount] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [selectedRow, setSelectedRow] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState("");
  const [newRecipientName, setNewRecipientName] = useState("");
  const [templateParams, setTemplateParams] = useState({});
  const hasActionPermission = permissions?.edit || permissions?.delete;

  // Customer dropdown state
  const [customerDropDownList, setCustomerDropDownList] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      campaign_name: "",
      template_id: "",
      media_url: "",
      schedule_type: "immediate",
      schedule_date: "",
      schedule_time: "",
    },

    mode: "onChange",
  });

  const selectedTemplateId = watch("template_id");
  const mediaUrl = watch("media_url");

  // Fetch campaigns
  const fetchCampaigns = async ({ all = false, perPage = null } = {}) => {
    try {
      setLoader(true);
      const params = {};
      if (all) params.all = 1;
      if (perPage) params.per_page = perPage;

      const response = await axios.get("/whatsapp/campaigns", { params });

      // Backend may return campaigns under different keys depending on pagination or 'all' flag.
      // Accept common shapes: { campaigns: [...] } or { data: [...] } or paginated structures.
      let campaigns = [];

      if (Array.isArray(response?.data?.campaigns))
        campaigns = response.data.campaigns;
      else if (Array.isArray(response?.data?.data))
        campaigns = response.data.data;
      else if (Array.isArray(response?.data)) campaigns = response.data;

      setFormData(campaigns || []);
      setCampaignCount(
        response?.data?.count ||
          response?.data?.total ||
          (campaigns ? campaigns.length : 0)
      );
    } catch (err) {
      toast.error(err?.message || "Failed to fetch campaigns.");
    } finally {
      setLoader(false);
    }
  };

  // Fetch approved templates
  // const fetchTemplates = async () => {
  //   try {
  //     const response = await axios.get("/getAllApprovedTemplates");
  //     const templateList = response?.data?.templates || [];
  //     console.log("Fetched templates:", templateList); // Debug log
  //     setTemplates(
  //       templateList.map((t) => ({
  //         id: t.id,
  //         name: t.name,
  //         language: t.language || "en_US",
  //         status: t.status,
  //         components: t.components || [],
  //       }))
  //     );
  //   } catch (err) {
  //     toast.error("Failed to fetch templates.");
  //     setTemplates([]);
  //   }
  // };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get("/getAllApprovedTemplates", {
        timeout: 15000, // 15 second timeout
      });

      const templateList = response?.data?.templates || [];
      console.log("✅ Fetched templates:", templateList.length);

      setTemplates(
        templateList.map((t) => ({
          id: t.id,
          name: t.name,
          language: t.language || "en_US",
          status: t.status,
          components: t.components || [],
        }))
      );

      if (templateList.length === 0) {
        toast.info("No approved templates found. Try syncing from Meta.");
      }
    } catch (err) {
      console.error("❌ Template fetch error:", err);

      if (err.code === "ECONNABORTED") {
        toast.error("Request timeout. Please try syncing templates first.");
      } else {
        toast.error("Failed to fetch templates. Check console for details.");
      }

      setTemplates([]);
    }
  };

  // Sync templates from Meta API to our DB via backend
  // const syncTemplates = async () => {
  //   try {
  //     setTemplatesSyncing(true);
  //     // Backend endpoint should call Meta API and persist templates into our DB
  //     const response = await axios.post("/syncTemplatesToDB");

  //     if (response?.data?.success) {
  //       toast.success(response.data.message || "Templates synced successfully");
  //     } else {
  //       toast.success(response?.data?.message || "Templates sync completed");
  //     }

  //     // Re-fetch templates from our DB endpoint
  //     await fetchTemplates();
  //   } catch (err) {
  //     console.error("Sync templates error", err);
  //     toast.error(err?.response?.data?.message || "Failed to sync templates.");
  //   } finally {
  //     setTemplatesSyncing(false);
  //   }
  // };

  const syncTemplates = async () => {
    try {
      setTemplatesSyncing(true);
      toast.loading("Syncing templates from Meta...", { id: "sync" });

      const response = await axios.post(
        "/syncTemplatesToDB",
        {},
        {
          timeout: 120000, // 2 minutes for sync operation
        }
      );

      if (response?.data?.success) {
        toast.success(
          response.data.message || "Templates synced successfully",
          { id: "sync" }
        );

        // Wait 2 seconds then fetch templates
        setTimeout(() => {
          fetchTemplates();
        }, 2000);
      } else {
        toast.success(response?.data?.message || "Sync completed", {
          id: "sync",
        });
        fetchTemplates();
      }
    } catch (err) {
      console.error("❌ Sync error:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to sync templates. Check backend logs.",
        { id: "sync" }
      );
    } finally {
      setTemplatesSyncing(false);
    }
  };

  // Fetch customer list
  const fetchCustomerList = async () => {
    try {
      const response = await axios.get("/getAllCustomerDetails");
      if (response?.data?.data?.length > 0) {
        setCustomerDropDownList(response.data.data);
      }
    } catch (err) {
      toast.error("Failed to fetch customer list.");
      setCustomerDropDownList([]);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchCustomerList();
  }, []);

  // Extract template parameters and content
  const extractTemplateInfo = (templateId) => {
    const template = templates.find((t) => t.id === templateId);

    console.log("Extracting info for template:", templateId); // Debug log
    console.log("Found template:", template); // Debug log

    if (!template || !template.components) {
      return {
        params: [],
        hasMedia: false,
        mediaType: null,
        headerText: "",
        bodyText: "",
        footerText: "",
        buttons: [],
      };
    }

    let params = [];
    let hasMedia = false;
    let mediaType = null;
    let headerText = "";
    let bodyText = "";
    let footerText = "";
    let buttons = [];

    template.components.forEach((component) => {
      console.log("Processing component:", component); // Debug log

      if (component.type === "HEADER") {
        if (
          component.format &&
          ["IMAGE", "VIDEO", "DOCUMENT"].includes(component.format)
        ) {
          hasMedia = true;
          mediaType = component.format.toLowerCase();
        } else if (component.format === "TEXT" && component.text) {
          headerText = component.text;
          const matches = component.text.matchAll(/\{\{(\d+)\}\}/g);
          for (const match of matches) {
            params.push({
              type: "header",
              key: match[1],
              label: `Header Parameter {{${match[1]}}}`,
            });
          }
        }
      }

      if (component.type === "BODY" && component.text) {
        bodyText = component.text;
        const matches = component.text.matchAll(/\{\{(\d+)\}\}/g);
        for (const match of matches) {
          params.push({
            type: "body",
            key: match[1],
            label: `Body Parameter {{${match[1]}}}`,
          });
        }
      }

      if (component.type === "FOOTER" && component.text) {
        footerText = component.text;
      }

      if (component.type === "BUTTONS" && component.buttons) {
        buttons = component.buttons;
      }
    });

    const result = {
      params,
      hasMedia,
      mediaType,
      headerText,
      bodyText,
      footerText,
      buttons,
    };
    console.log("Extracted template info:", result); // Debug log

    return result;
  };

  // Get current template info
  const templateInfo = selectedTemplateId
    ? extractTemplateInfo(selectedTemplateId)
    : {
        params: [],
        hasMedia: false,
        mediaType: null,
        headerText: "",
        bodyText: "",
        footerText: "",
        buttons: [],
      };

  // Generate preview text with parameters replaced
  const generatePreviewText = () => {
    let previewHeader = templateInfo.headerText || "";
    let previewBody = templateInfo.bodyText || "";
    let previewFooter = templateInfo.footerText || "";

    // Replace parameters with actual values or placeholders
    templateInfo.params.forEach((param) => {
      const value = templateParams[param.key] || `[${param.label}]`;
      const regex = new RegExp(`\\{\\{${param.key}\\}\\}`, "g");

      if (param.type === "header") {
        previewHeader = previewHeader.replace(regex, value);
      } else if (param.type === "body") {
        previewBody = previewBody.replace(regex, value);
      }
    });

    return {
      header: previewHeader,
      body: previewBody,
      footer: previewFooter,
    };
  };

  const previewText = generatePreviewText();

  // Get selected template object
  const selectedTemplateObj = templates.find(
    (t) => t.id === selectedTemplateId
  );

  // Handle dialog open
  const handleOpenDialog = () => {
    setSelectedRow(null);
    setRecipients([]);
    setSelectedCustomers([]);
    setTemplateParams({});
    reset({
      campaign_name: "",
      template_id: "",
      media_url: "",
      schedule_type: "immediate",
      schedule_date: "",
      schedule_time: "",
    });
    setOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedRow(null);
    setRecipients([]);
    setSelectedCustomers([]);
    setTemplateParams({});
    reset();
  };

  // Handle edit
  const handleEditRow = (row) => {
    setSelectedRow(row);
    setRecipients(row.recipients || []);
    setTemplateParams(row.template_params || {});

    // Load selected customers if available
    if (row.selected_customers) {
      setSelectedCustomers(row.selected_customers);
    }

    reset({
      campaign_name: row.campaign_name || "",
      template_id: row.template_id || "",
      media_url: row.media_url || "",
      schedule_type: row.schedule_type || "immediate",
      schedule_date: row.schedule_date || "",
      schedule_time: row.schedule_time || "",
    });
    setOpen(true);
  };

  // Add customers from dropdown
  const handleCustomerSelection = (event, newValue) => {
    if (newValue && newValue.length > 0) {
      const newRecipients = newValue
        .filter(
          (customer) =>
            !recipients.some((r) => r.phone === customer.customer_mobile)
        )
        .map((customer) => ({
          phone: customer.customer_mobile,
          name: customer.customer_name,
          customer_id: customer.customer_id,
        }));

      setRecipients([...recipients, ...newRecipients]);
      setSelectedCustomers(newValue);

      if (newRecipients.length > 0) {
        toast.success(`${newRecipients.length} customer(s) added`);
      }
    } else {
      setSelectedCustomers([]);
    }
  };

  // Add recipient manually
  const addRecipient = () => {
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!newRecipient) {
      toast.error("Please enter a phone number");
      return;
    }

    if (!phoneRegex.test(newRecipient)) {
      toast.error("Please enter a valid 10-digit Indian phone number");
      return;
    }

    if (!recipients.some((r) => r.phone === newRecipient)) {
      setRecipients([
        ...recipients,
        {
          phone: newRecipient,
          name: newRecipientName || "",
        },
      ]);
      setNewRecipient("");
      setNewRecipientName("");
      toast.success("Recipient added successfully");
    } else {
      toast.error("This number is already added");
    }
  };

  // Remove recipient
  const removeRecipient = (phone) => {
    setRecipients(recipients.filter((r) => r.phone !== phone));

    // Also remove from selected customers if exists
    setSelectedCustomers(
      selectedCustomers.filter((c) => c.customer_mobile !== phone)
    );

    toast.success("Recipient removed");
  };

  // Handle CSV upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const lines = text.split("\n");
        const headers = lines[0]
          .toLowerCase()
          .split(",")
          .map((h) => h.trim());

        const phoneIndex = headers.findIndex(
          (h) =>
            h.includes("phone") || h.includes("mobile") || h.includes("number")
        );
        const nameIndex = headers.findIndex((h) => h.includes("name"));

        const phoneRegex = /^[6-9]\d{9}$/;
        const newRecipients = lines
          .slice(1)
          .filter((line) => line.trim())
          .map((line) => {
            const values = line.split(",").map((v) => v.trim());
            const phone = values[phoneIndex] || values[0];

            if (!phoneRegex.test(phone)) {
              return null;
            }

            return {
              phone: phone,
              name: values[nameIndex] || "",
            };
          })
          .filter((r) => r !== null);

        setRecipients([...recipients, ...newRecipients]);
        toast.success(
          `${newRecipients.length} recipients imported successfully`
        );
      };
      reader.readAsText(file);
    }
  };

  // Update template parameter
  const updateTemplateParam = (key, value) => {
    setTemplateParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Submit form
  const onSubmit = async (data) => {
    try {
      if (recipients.length === 0) {
        toast.error("Please add at least one recipient");
        return;
      }

      if (templateInfo.hasMedia && !data.media_url) {
        toast.error(`This template requires a ${templateInfo.mediaType} URL`);
        return;
      }

      const selectedTemplate = templates.find((t) => t.id === data.template_id);
      if (!selectedTemplate) {
        toast.error("Selected template not found");
        return;
      }

      const normalizePhone = (raw) => {
        if (!raw) return "";
        let digits = String(raw).replace(/\D+/g, "");
        if (digits.startsWith("91") && digits.length > 10) return digits;
        if (digits.length === 10) return "91" + digits;
        return digits;
      };

      const preparedRecipients = recipients.map((r) => {
        const custom_fields = {};

        templateInfo.params.forEach((param) => {
          const key = param.key;
          custom_fields[`var_${key}`] =
            templateParams[key] || r.name || "Customer";
        });

        return {
          phone: normalizePhone(r.phone),
          name: r.name || "Customer",
          custom_fields,
          customer_id: r.customer_id || null,
        };
      });

      const payload = {
        campaign_name: data.campaign_name,
        template_name: selectedTemplate.name,
        template_data: selectedTemplate,
        template_language: selectedTemplate.language || "en_US",
        recipients: preparedRecipients,
        selected_customers: selectedCustomers,
        schedule_type: data.schedule_type,
        schedule_date: data.schedule_date,
        schedule_time: data.schedule_time,
        media_urls: data.media_url ? [data.media_url] : [],
      };

      if (selectedRow) {
        await axios.put(`/whatsapp/campaigns/${selectedRow.id}`, payload);
        toast.success("Campaign updated successfully!");
      } else {
        await axios.post("/whatsapp/campaign/send", payload);
        toast.success("Campaign created successfully!");
      }

      handleCloseDialog();
      fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save campaign.");
    }
  };

  // Table columns
  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
      },
    },
    {
      name: "message_count",
      label: "Msg Count",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <Typography>{formData[dataIndex]?.message_count ?? 0}</Typography>
        ),
      },
    },
    {
      name: "failed_count",
      label: "Failed",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <Typography sx={{ color: red[600] }}>
            {formData[dataIndex]?.failed_count ?? 0}
          </Typography>
        ),
      },
    },
    {
      name: "sent_count",
      label: "Sent",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <Typography sx={{ color: green[700] }}>
            {formData[dataIndex]?.sent_count ?? 0}
          </Typography>
        ),
      },
    },
    {
      name: "delivered_count",
      label: "Delivered",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <Typography>{formData[dataIndex]?.delivered_count ?? 0}</Typography>
        ),
      },
    },
    {
      name: "read_count",
      label: "Read",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <Typography>{formData[dataIndex]?.read_count ?? 0}</Typography>
        ),
      },
    },
    {
      name: "total_batches",
      label: "Total Batches",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <Typography sx={{ fontWeight: 600 }}>
            {formData[dataIndex]?.total_batches ?? 0}
          </Typography>
        ),
      },
    },
    {
      name: "current_batch",
      label: "Current Batch",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const current = formData[dataIndex]?.current_batch ?? 0;
          const total = formData[dataIndex]?.total_batches ?? 0;
          const progress = total > 0 ? ((current / total) * 100).toFixed(0) : 0;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>
                {current} / {total}
              </Typography>
              <Chip
                label={`${progress}%`}
                size="small"
                variant="outlined"
                color={
                  progress >= 100
                    ? "success"
                    : progress >= 50
                    ? "warning"
                    : "error"
                }
              />
            </Box>
          );
        },
      },
    },
    {
      name: "campaign_name",
      label: "Campaign Name",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <p>{formData[dataIndex]?.campaign_name}</p>
        ),
      },
    },
    {
      name: "template_name",
      label: "Template",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <p>{formData[dataIndex]?.template_name || "N/A"}</p>
        ),
      },
    },
    {
      name: "recipients",
      label: "Recipients",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const recipients = formData[dataIndex]?.recipients || [];
          return (
            <Chip
              label={`${recipients.length} recipients`}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        },
      },
    },
    {
      name: "schedule_type",
      label: "Schedule",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const type = formData[dataIndex]?.schedule_type;
          return (
            <Chip
              label={type === "immediate" ? "Immediate" : "Scheduled"}
              size="small"
              color={type === "immediate" ? "success" : "warning"}
            />
          );
        },
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.status || "pending";
          const statusColors = {
            sent: "success",
            pending: "warning",
            failed: "error",
          };
          return (
            <Chip
              label={status.toUpperCase()}
              size="small"
              color={statusColors[status] || "default"}
            />
          );
        },
      },
    },
    ...(permissions?.edit
      ? [
          {
            name: "Edit",
            label: "Edit",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const row = formData[dataIndex];
                return (
                  <Button aria-label="edit" onClick={() => handleEditRow(row)}>
                    <EditIcon
                      fontSize="medium"
                      sx={{ color: "secondary.main" }}
                    />
                  </Button>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    download: true,
    viewColumns: true,
    print: false,
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4>WhatsApp Campaigns</h4>
          {viewMode === "table" ? (
            <MUIDataTable
              title={
                permissions.add ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleOpenDialog}
                    >
                      Create Campaign
                    </Button>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        title={
                          viewMode === "table"
                            ? "Switch to dashboard view"
                            : "Switch to table view"
                        }
                        size="small"
                        variant={viewMode === "dashboard" ? "outlined" : "text"}
                        startIcon={<DashboardCustomizeIcon />}
                        onClick={() =>
                          setViewMode((m) =>
                            m === "table" ? "dashboard" : "table"
                          )
                        }
                      >
                        {viewMode === "dashboard" ? "Table view" : "Dashboard"}
                      </Button>
                      <Button
                        size="small"
                        variant={showAll ? "outlined" : "text"}
                        startIcon={<TableRowsIcon />}
                        onClick={async () => {
                          // Toggle: if showAll -> show recent; else show all
                          const next = !showAll;
                          setShowAll(next);
                          await fetchCampaigns({ all: next });
                        }}
                      >
                        {showAll ? "Showing all" : "Show all"}
                      </Button>
                      {/* quick count display if available */}
                      {campaignCount !== null && (
                        <Typography
                          variant="caption"
                          sx={{ ml: 1, color: "text.secondary" }}
                        >
                          {campaignCount} items
                        </Typography>
                      )}
                    </Box>
                  </div>
                ) : null
              }
              data={formData}
              columns={columns}
              options={options}
            />
          ) : (
            // Dashboard summary view
            <Box>
              <Box
                mb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="h6">Campaign Dashboard</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Showing {campaignCount ?? formData.length} campaign(s)
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setViewMode("table")}>
                  Back to table
                </Button>
              </Box>

              <Grid container spacing={2}>
                {formData && formData.length > 0 ? (
                  formData.map((c) => (
                    <Grid item xs={12} md={6} lg={4} key={c.id}>
                      <Paper sx={{ p: 2 }} elevation={2}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="start"
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {c.campaign_name || "Untitled"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Template: {c.template_name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Created:{" "}
                              {c.created_at
                                ? new Date(c.created_at).toLocaleString()
                                : "—"}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Chip
                              label={`${
                                (c.recipients || []).length
                              } recipients`}
                              size="small"
                            />
                            <Box mt={1}>
                              <Chip
                                label={(c.status || "pending").toUpperCase()}
                                size="small"
                                color={
                                  c.status === "sent"
                                    ? "success"
                                    : c.status === "failed"
                                    ? "error"
                                    : "warning"
                                }
                              />
                            </Box>
                            {/* Message counts */}
                            <Box
                              mt={1}
                              display="flex"
                              gap={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                            >
                              <Chip
                                label={`Msgs: ${c.message_count ?? 0}`}
                                size="small"
                              />
                              <Chip
                                label={`Sent: ${c.sent_count ?? 0}`}
                                size="small"
                                color="success"
                              />
                              <Chip
                                label={`Failed: ${c.failed_count ?? 0}`}
                                size="small"
                                color="error"
                              />
                              <Chip
                                label={`Delivered: ${c.delivered_count ?? 0}`}
                                size="small"
                              />
                              <Chip
                                label={`Read: ${c.read_count ?? 0}`}
                                size="small"
                              />
                            </Box>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {c.description || c.template_data?.body || ""}
                        </Typography>

                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => handleEditRow(c)}>
                            Edit
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: "center" }}>
                      <Typography color="text.secondary">
                        No campaigns to show
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Campaign Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsAppIcon sx={{ color: green[500] }} />
            {selectedRow ? "Edit Campaign" : "Create New Campaign"}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Campaign Name */}
              <Grid item xs={12}>
                <Controller
                  name="campaign_name"
                  control={control}
                  rules={{ required: "Campaign name is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Campaign Name"
                      fullWidth
                      error={!!errors.campaign_name}
                      helperText={errors.campaign_name?.message}
                      placeholder="e.g., New Year 2025 Campaign"
                    />
                  )}
                />
              </Grid>

              {/* Template Selection */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box flex={1}>
                    <Controller
                      name="template_id"
                      control={control}
                      rules={{ required: "Template is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Select WhatsApp Template"
                          fullWidth
                          select
                          SelectProps={{ native: true }}
                          InputLabelProps={{
                            shrink: !!field.value || templates.length === 0,
                          }}
                          error={!!errors.template_id}
                          helperText={errors.template_id?.message}
                          disabled={templates.length === 0}
                          sx={{ minHeight: 56 }}
                        >
                          <option value="">
                            {templates.length === 0
                              ? "No templates available - Click sync button →"
                              : "Choose a template"}
                          </option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} ({template.language}) -{" "}
                              {template.status}
                            </option>
                          ))}
                        </TextField>
                      )}
                    />
                  </Box>

                  <IconButton
                    size="small"
                    aria-label="sync-templates"
                    onClick={syncTemplates}
                    disabled={templatesSyncing}
                    title="Sync templates from Meta to DB"
                    color="primary"
                  >
                    {templatesSyncing ? (
                      <CircularProgress size={18} />
                    ) : (
                      <RefreshIcon />
                    )}
                  </IconButton>
                </Box>

                {templates.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No templates loaded. Click the refresh button to sync from
                    Meta.
                  </Alert>
                )}

                {/* Template Info Card */}
                {selectedTemplateObj && (
                  <Paper
                    elevation={1}
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "#f0fdf4",
                      border: "1px solid #86efac",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color={green[800]}
                      mb={1}
                    >
                      📋 Template Information
                    </Typography>
                    <Box sx={{ fontSize: "0.875rem", color: grey[700] }}>
                      <Typography variant="body2">
                        <strong>Name:</strong> {selectedTemplateObj.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Language:</strong>{" "}
                        {selectedTemplateObj.language}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Status:</strong> {selectedTemplateObj.status}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Parameters:</strong>{" "}
                        {templateInfo.params.length > 0
                          ? `${templateInfo.params.length} parameter(s)`
                          : "None"}
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Grid>

              {/* Template Preview */}
              {selectedTemplateId &&
                (templateInfo.bodyText || templateInfo.headerText) && (
                  <Grid item xs={12}>
                    <Paper
                      elevation={3}
                      sx={{
                        bgcolor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: green[600],
                          px: 2,
                          py: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <WhatsAppIcon sx={{ color: "white", fontSize: 22 }} />
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          color="white"
                        >
                          📱 Template Preview
                        </Typography>
                      </Box>

                      {/* WhatsApp-style message container */}
                      <Box sx={{ p: 3, bgcolor: "#e5ddd5", minHeight: 250 }}>
                        <Paper
                          elevation={2}
                          sx={{
                            maxWidth: 420,
                            bgcolor: "white",
                            borderRadius: "8px",
                            overflow: "hidden",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
                          }}
                        >
                          {/* Media placeholder */}
                          {templateInfo.hasMedia && (
                            <Box
                              sx={{
                                bgcolor: grey[200],
                                height: 200,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                color: grey[600],
                                borderBottom: "1px solid #e5e7eb",
                                gap: 1,
                              }}
                            >
                              {templateInfo.mediaType === "image" && (
                                <>
                                  <ImageIcon
                                    sx={{ fontSize: 64, color: grey[500] }}
                                  />
                                  <Typography variant="body2" fontWeight="500">
                                    {mediaUrl
                                      ? "Image will be displayed here"
                                      : "Image Placeholder"}
                                  </Typography>
                                  {mediaUrl && (
                                    <Typography
                                      variant="caption"
                                      color={grey[600]}
                                      sx={{ px: 2, textAlign: "center" }}
                                    >
                                      {mediaUrl.length > 40
                                        ? mediaUrl.substring(0, 40) + "..."
                                        : mediaUrl}
                                    </Typography>
                                  )}
                                </>
                              )}
                              {templateInfo.mediaType === "video" && (
                                <>
                                  <VideoLibraryIcon
                                    sx={{ fontSize: 64, color: grey[500] }}
                                  />
                                  <Typography variant="body2" fontWeight="500">
                                    Video Placeholder
                                  </Typography>
                                </>
                              )}
                              {templateInfo.mediaType === "document" && (
                                <>
                                  <DescriptionIcon
                                    sx={{ fontSize: 64, color: grey[500] }}
                                  />
                                  <Typography variant="body2" fontWeight="500">
                                    Document Placeholder
                                  </Typography>
                                </>
                              )}
                            </Box>
                          )}

                          {/* Message content */}
                          <Box sx={{ p: 2.5 }}>
                            {/* Header */}
                            {previewText.header && (
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{
                                  mb: 1.5,
                                  color: grey[900],
                                  fontSize: "1rem",
                                }}
                              >
                                {previewText.header}
                              </Typography>
                            )}

                            {/* Body */}
                            {previewText.body && (
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  color: grey[800],
                                  lineHeight: 1.6,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {previewText.body}
                              </Typography>
                            )}

                            {/* Footer */}
                            {previewText.footer && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 1.5,
                                  color: grey[600],
                                  fontStyle: "italic",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {previewText.footer}
                              </Typography>
                            )}

                            {/* Buttons */}
                            {templateInfo.buttons &&
                              templateInfo.buttons.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    borderTop: "1px solid #e5e7eb",
                                    pt: 1,
                                  }}
                                >
                                  {templateInfo.buttons.map((button, idx) => (
                                    <Button
                                      key={idx}
                                      fullWidth
                                      variant="text"
                                      sx={{
                                        justifyContent: "center",
                                        color: blue[600],
                                        textTransform: "none",
                                        fontSize: "0.875rem",
                                        py: 1,
                                        fontWeight: 500,
                                        "&:hover": {
                                          bgcolor: blue[50],
                                        },
                                      }}
                                    >
                                      {button.type === "URL" && "🔗 "}
                                      {button.type === "PHONE_NUMBER" && "📞 "}
                                      {button.text}
                                    </Button>
                                  ))}
                                </Box>
                              )}
                          </Box>

                          {/* Timestamp */}
                          <Box
                            sx={{
                              px: 2.5,
                              pb: 1,
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: grey[500], fontSize: "0.7rem" }}
                            >
                              {new Date().toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                            <Box
                              component="span"
                              sx={{ color: grey[400], fontSize: "0.7rem" }}
                            >
                              ✓✓
                            </Box>
                          </Box>
                        </Paper>

                        {/* Info note */}
                        <Alert
                          severity="info"
                          icon="💡"
                          sx={{ mt: 2, maxWidth: 420 }}
                        >
                          This is how your message will appear to recipients on
                          WhatsApp
                        </Alert>
                      </Box>
                    </Paper>
                  </Grid>
                )}

              {/* Template Parameters */}
              {templateInfo.params.length > 0 && (
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2, bgcolor: "#f0f9ff" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      mb={2}
                      color="primary"
                    >
                      ✏️ Template Parameters
                    </Typography>
                    <Grid container spacing={2}>
                      {templateInfo.params.map((param, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <TextField
                            label={param.label}
                            value={templateParams[param.key] || ""}
                            onChange={(e) =>
                              updateTemplateParam(param.key, e.target.value)
                            }
                            fullWidth
                            size="small"
                            placeholder={`Enter value for {{${param.key}}}`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      💡 These values will be applied to all recipients. The
                      preview above updates as you type!
                    </Alert>
                  </Paper>
                </Grid>
              )}

              {/* Media URL */}
              {templateInfo.hasMedia && (
                <Grid item xs={12}>
                  <Controller
                    name="media_url"
                    control={control}
                    rules={{
                      required: `${templateInfo.mediaType} URL is required`,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`${templateInfo.mediaType?.toUpperCase()} URL`}
                        fullWidth
                        error={!!errors.media_url}
                        helperText={
                          errors.media_url?.message ||
                          "Enter publicly accessible URL"
                        }
                        placeholder={`https://example.com/${templateInfo.mediaType}.jpg`}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Schedule Type */}
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: "#fff7ed" }}>
                  <Controller
                    name="schedule_type"
                    control={control}
                    render={({ field }) => (
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          Schedule Campaign
                        </FormLabel>
                        <RadioGroup {...field} row>
                          <FormControlLabel
                            value="immediate"
                            control={<Radio />}
                            label="Send Immediately"
                          />
                          <FormControlLabel
                            value="scheduled"
                            control={<Radio />}
                            label="Schedule for Later"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />

                  {watch("schedule_type") === "scheduled" && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Controller
                          name="schedule_date"
                          control={control}
                          rules={{
                            required:
                              watch("schedule_type") === "scheduled"
                                ? "Date is required"
                                : false,
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              type="date"
                              label="Schedule Date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              inputProps={{
                                min: new Date().toISOString().split("T")[0],
                              }}
                              error={!!errors.schedule_date}
                              helperText={errors.schedule_date?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name="schedule_time"
                          control={control}
                          rules={{
                            required:
                              watch("schedule_type") === "scheduled"
                                ? "Time is required"
                                : false,
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              type="time"
                              label="Schedule Time"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              error={!!errors.schedule_time}
                              helperText={errors.schedule_time?.message}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Paper>
              </Grid>

              {/* Recipients Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Recipients ({recipients.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    size="small"
                  >
                    Upload CSV
                    <input
                      type="file"
                      hidden
                      accept=".csv"
                      onChange={handleFileUpload}
                    />
                  </Button>
                </Box>

                {/* Customer Selection Dropdown */}
                <Box mb={3}>
                  <Paper elevation={2} sx={{ p: 2, bgcolor: blue[50] }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PersonAddIcon sx={{ color: blue[700] }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color={blue[900]}
                      >
                        Select from Customer List
                      </Typography>
                    </Box>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={customerDropDownList}
                      isOptionEqualToValue={(option, value) =>
                        option?.customer_id === value?.customer_id
                      }
                      getOptionLabel={(option) =>
                        `${option.customer_name} - ${option.customer_mobile}`
                      }
                      value={selectedCustomers}
                      onChange={handleCustomerSelection}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          {option.customer_name} - {option.customer_mobile}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Search and select customers..."
                          size="small"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={`${option.customer_name} - ${option.customer_mobile}`}
                            {...getTagProps({ index })}
                            size="small"
                            color="primary"
                          />
                        ))
                      }
                    />
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Select multiple customers from your database
                    </Alert>
                  </Paper>
                </Box>

                {/* Recipients List */}
                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 300,
                    overflow: "auto",
                    bgcolor: grey[50],
                  }}
                >
                  {recipients.length === 0 ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      p={4}
                    >
                      <Typography color="textSecondary">
                        No recipients added yet
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Add from customer list, manually, or upload CSV
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {recipients.map((recipient, idx) => (
                        <ListItem key={idx} divider>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography>{recipient.phone}</Typography>
                                {recipient.customer_id && (
                                  <Chip
                                    label="Customer"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={recipient.name}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => removeRecipient(recipient.phone)}
                            >
                              <DeleteIcon sx={{ color: red[500] }} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              variant="contained"
              color="error"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              sx={{
                background: `linear-gradient(135deg, ${green[500]} 0%, ${green[700]} 100%)`,
              }}
            >
              {selectedRow ? "Update Campaign" : "Send Campaign"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default WhatsAppCampaignScreen;
